import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getProgramBySlug, getSubjectBySlug, getIntentById, getUtilityById, getAeoIntentById, INTENTS, HOWTO_INTENT, YEAR_INTENT, CATEGORY_INTENT, SUBJECT_INTENT, CATEGORY_ONLINE_INTENT, CATEGORY_FEES_INTENT, CATEGORY_2026_INTENT, LOCALITY_UTILITY_INTENT, getProgramsByLocation } from "@/data/programs";
import { getCourseBySlug, getCoursesByCategory, courseCategories, courses } from "@/data/courses";
import { getLocationBySlug, locations } from "@/data/locations";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Phone, MessageCircle, MapPin, CheckCircle, ChevronRight, HelpCircle, Star, Trophy, BookOpen, Target, Clock, GraduationCap, Sparkles } from "lucide-react";

const SITE = "https://vedantiacademy.com";
const PHONE = "+918308776505";
const MAPS_URL = "https://maps.app.goo.gl/?q=Vedanti+Academy+Badlapur+Jadhav+Market+Aradhana+Hotel";
const ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "3rd Floor, Jadhav Market, above Aradhana Hotel",
  addressLocality: "Badlapur",
  addressRegion: "Maharashtra",
  postalCode: "421503",
  addressCountry: "IN",
};

// Build an intent-aware, lead-qualifying WhatsApp pre-fill message
function buildWaMessage(ctx, prog) {
  const { loc, course, category, subject, intentLabel } = ctx;
  const target = course?.fullName || course?.name || category?.name || subject?.name || "competitive exam coaching";
  const intentQ = (() => {
    switch (prog.intent) {
      case "fees":   return "• Latest fee chart + EMI / installment options";
      case "online": return "• Online class schedule, recordings & live-class link";
      case "mock":   return "• Free demo mock test + access to full mock series";
      case "best":   return "• Why Vedanti Academy + faculty profile";
      case "near":   return "• Centre directions, batch timings & weekend options";
      default:       return "• Latest batch schedule & fees";
    }
  })();
  const courseLine = course ? `*Course:* ${course.fullName || course.name} (${course.name})` : (category ? `*Category:* ${category.name}` : (subject ? `*Subject:* ${subject.name}` : ""));
  return `Hi Vedanti Academy 👋\n\nI'm from *${loc.name}* and interested in *${intentLabel}* — ${target}.\n${courseLine}\n\nCould you please share:\n${intentQ}\n• Free demo class slot\n• Upcoming batch start dates\n\nThanks!`;
}

const waLink = (msg) => `https://wa.me/${PHONE.replace("+", "")}?text=${encodeURIComponent(msg)}`;

function notFound() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
      <h1 className="text-3xl font-bold text-vedanti-navy mb-4">Program Not Found</h1>
      <p className="text-slate-500 mb-6">The page you are looking for may have moved.</p>
      <Link to="/programs" className="text-vedanti-orange hover:underline">View all programs</Link>
    </div>
  );
}

// Build content payload for any program type by parsing the program object
function buildContent(prog) {
  const loc = getLocationBySlug(prog.locationSlug);
  if (!loc) return null;

  // Course/Category/Subject context
  const course = prog.courseSlug ? getCourseBySlug(prog.courseSlug) : null;
  const category = prog.categoryId ? courseCategories.find((c) => c.id === prog.categoryId) : null;
  const subject = prog.subjectSlug ? getSubjectBySlug(prog.subjectSlug) : null;

  let h1, title, desc, intro, primaryAnswer, kicker, intentLabel;

  if (prog.type === "intent-course-location") {
    const intent = getIntentById(prog.intent);
    if (!intent || !course) return null;
    h1 = intent.h1(course, loc);
    title = intent.title(course, loc);
    desc = intent.desc(course, loc);
    intro = intent.intro(course, loc);
    primaryAnswer = intent.answer(course, loc);
    kicker = `${intent.label} ${course.name}`;
    intentLabel = intent.label;
  } else if (prog.type === "category-location") {
    if (!category) return null;
    h1 = CATEGORY_INTENT.h1(category, loc);
    title = CATEGORY_INTENT.title(category, loc);
    desc = CATEGORY_INTENT.desc(category, loc);
    intro = CATEGORY_INTENT.intro(category, loc);
    primaryAnswer = CATEGORY_INTENT.answer(category, loc);
    kicker = `Best ${category.name}`;
    intentLabel = "Best";
  } else if (prog.type === "subject-location") {
    if (!subject) return null;
    h1 = SUBJECT_INTENT.h1(subject, loc);
    title = SUBJECT_INTENT.title(subject, loc);
    desc = SUBJECT_INTENT.desc(subject, loc);
    intro = SUBJECT_INTENT.intro(subject, loc);
    primaryAnswer = SUBJECT_INTENT.answer(subject, loc);
    kicker = subject.name;
    intentLabel = "Subject";
  } else if (prog.type === "year-course-location") {
    if (!course) return null;
    h1 = YEAR_INTENT.h1(course, loc);
    title = YEAR_INTENT.title(course, loc);
    desc = YEAR_INTENT.desc(course, loc);
    intro = YEAR_INTENT.intro(course, loc);
    primaryAnswer = YEAR_INTENT.answer(course, loc);
    kicker = `${course.name} 2026`;
    intentLabel = "2026 Batch";
  } else if (prog.type === "howto-course-location") {
    if (!course) return null;
    h1 = HOWTO_INTENT.h1(course, loc);
    title = HOWTO_INTENT.title(course, loc);
    desc = HOWTO_INTENT.desc(course, loc);
    intro = HOWTO_INTENT.intro(course, loc);
    primaryAnswer = HOWTO_INTENT.answer(course, loc);
    kicker = `How To Crack ${course.name}`;
    intentLabel = "Strategy";
  } else if (prog.type === "category-online-location") {
    if (!category) return null;
    h1 = CATEGORY_ONLINE_INTENT.h1(category, loc);
    title = CATEGORY_ONLINE_INTENT.title(category, loc);
    desc = CATEGORY_ONLINE_INTENT.desc(category, loc);
    intro = CATEGORY_ONLINE_INTENT.intro(category, loc);
    primaryAnswer = CATEGORY_ONLINE_INTENT.answer(category, loc);
    kicker = `Online ${category.name}`;
    intentLabel = "Online";
  } else if (prog.type === "category-fees-location") {
    if (!category) return null;
    h1 = CATEGORY_FEES_INTENT.h1(category, loc);
    title = CATEGORY_FEES_INTENT.title(category, loc);
    desc = CATEGORY_FEES_INTENT.desc(category, loc);
    intro = CATEGORY_FEES_INTENT.intro(category, loc);
    primaryAnswer = CATEGORY_FEES_INTENT.answer(category, loc);
    kicker = `${category.name} Fees`;
    intentLabel = "Fees";
  } else if (prog.type === "category-2026-location") {
    if (!category) return null;
    h1 = CATEGORY_2026_INTENT.h1(category, loc);
    title = CATEGORY_2026_INTENT.title(category, loc);
    desc = CATEGORY_2026_INTENT.desc(category, loc);
    intro = CATEGORY_2026_INTENT.intro(category, loc);
    primaryAnswer = CATEGORY_2026_INTENT.answer(category, loc);
    kicker = `${category.name} 2026`;
    intentLabel = "2026 Batch";
  } else if (prog.type === "locality-utility") {
    const util = prog.utilityId ? getUtilityById(prog.utilityId) : LOCALITY_UTILITY_INTENT;
    if (!util) return null;
    h1 = util.h1(loc);
    title = util.title(loc);
    desc = util.desc(loc);
    intro = util.intro(loc);
    primaryAnswer = util.answer(loc);
    kicker = util.kicker || "Free Resources";
    intentLabel = util.intentLabel || "Free";
  } else if (prog.type === "aeo-question") {
    const aeo = getAeoIntentById(prog.aeoIntent);
    if (!aeo || !course) return null;
    h1 = aeo.h1(course, loc);
    title = aeo.title(course, loc);
    desc = aeo.desc(course, loc);
    intro = aeo.intro(course, loc);
    primaryAnswer = aeo.answer(course, loc);
    kicker = aeo.kicker;
    intentLabel = aeo.intentLabel;
  } else {
    return null;
  }

  return { h1, title, desc, intro, primaryAnswer, kicker, intentLabel, loc, course, category, subject };
}

function buildFaqs(ctx, prog) {
  const { loc, course, category, subject } = ctx;
  const subjectName = subject?.name;
  const target = course?.name || category?.name || subjectName || "competitive exam";
  const isFees = prog.intent === "fees";
  const isOnline = prog.intent === "online";
  const isMock = prog.intent === "mock";
  const isHow = prog.type === "howto-course-location";

  const base = [
    {
      q: isFees
        ? `What are the ${target} coaching fees in ${loc.name} at Vedanti Academy?`
        : `What does ${target} coaching at Vedanti Academy include for ${loc.name} students?`,
      a: isFees
        ? `Vedanti Academy offers ${target} coaching at affordable, all-inclusive fees with EMI / installment options for ${loc.name} students. Every package includes live online + offline classes, printed study material, daily quizzes, full-length mocks, and 'Upto Selection Support'. Call +91 8308776505 for the latest fee chart.`
        : `Our ${target} coaching for ${loc.name} students includes daily live classes (online + offline), printed study material, daily topic-wise quizzes, sectional and full-length mock tests, doubt-solving via WhatsApp/Zoom, and 'Upto Selection Support' until you crack the exam.`,
    },
    {
      q: `Where is Vedanti Academy located, and how can ${loc.name} students reach it?`,
      a: `Vedanti Academy's flagship branch is at 3rd Floor, Jadhav Market, above Aradhana Hotel, Badlapur (Maharashtra 421503), 2 minutes from Badlapur Railway Station. ${loc.connectivity || `${loc.name} is ${loc.distance || "easily reachable"} from our centre.`} ${loc.name} students can also enrol in our 100% online batch.`,
    },
    {
      q: isOnline
        ? `Is online ${target} coaching effective for ${loc.name} students?`
        : `Can ${loc.name} students attend ${target} classes online from home?`,
      a: `Yes. Our 360° hybrid model means ${loc.name} students attend live HD classes online, get recorded lectures for revision, daily quizzes, weekly mocks, and rapid WhatsApp/Zoom doubt support. The course content and outcomes are identical to our offline batch — over 31 students have cracked competitive exams via this hybrid path.`,
    },
    {
      q: `What is the eligibility for ${course?.name || target}?`,
      a: course?.eligibility || `Eligibility varies by exam stream. For Banking and most government exams, a graduate degree (any stream) and age 18–30 are typical. For SSC MTS / CHSL, 10th / 12th pass is sufficient. For MBA entrance exams, a graduate degree is required. Call +91 8308776505 for exact eligibility details.`,
    },
    {
      q: isMock
        ? `Are ${target} mock tests free for ${loc.name} students?`
        : isHow
        ? `How long does it take ${loc.name} students to crack ${course?.name}?`
        : `How long does it take to prepare for ${target} from ${loc.name}?`,
      a: isMock
        ? `New ${loc.name} students get the first ${target} mock test absolutely free. After that, our complete mock series — 50+ sectional + 25+ full-length tests — is included free with every Vedanti Academy course package.`
        : `Most ${loc.name} students at Vedanti Academy become ${course?.name || target}-ready in 4–6 months of focused preparation. With our 'Upto Selection Support', you continue attending classes until you crack the exam — no fixed end date.`,
    },
    {
      q: `What batch timings are available for ${loc.name} students?`,
      a: `We offer Morning (8–10 AM), Afternoon (12–2 PM), Evening (5–7 PM) and Weekend batches. ${loc.name} students can pick the slot that best fits their schedule and commute. Online batches mirror these timings.`,
    },
    {
      q: `Is there a free demo class for ${target} for ${loc.name} students?`,
      a: `Absolutely — Vedanti Academy offers a free demo class for every course. ${loc.name} students can book a demo at /demo-class on our site, call +91 8308776505, or WhatsApp us. Demos are available both online and offline at our Badlapur branch.`,
    },
    {
      q: `What is the success rate of Vedanti Academy?`,
      a: `Vedanti Academy has produced 31+ documented achievers across Banking (IBPS, SBI, RBI, RRB), SSC, Railway, LIC and MBA CET, with 99 percentile MBA CET scorers and 95 percentile CAT scorers. We hold 500+ Google reviews with a 5-star rating.`,
    },
  ];

  if (subject) {
    base.push({
      q: `Which exams does the ${subject.name} module help me crack?`,
      a: `The ${subject.name} module at Vedanti Academy applies to ${subject.appliesTo}. Major topics covered include ${subject.topics.join(", ")}.`,
    });
  }

  return base;
}

// Internal-linking — generate cross-links to nearby programs, related courses, related locations
function buildCrossLinks(ctx, prog) {
  const { loc, course, category } = ctx;
  const links = [];

  // Other intents for same course+location
  if (course) {
    INTENTS.filter((i) => i.id !== prog.intent).slice(0, 4).forEach((i) => {
      links.push({ to: `/programs/${i.slugFn(course, loc)}`, label: `${i.label} ${course.name} in ${loc.name}` });
    });
    // Year + Howto variants
    links.push({ to: `/programs/${YEAR_INTENT.slugFn(course, loc)}`, label: `${course.name} 2026 Coaching in ${loc.name}` });
    links.push({ to: `/programs/${HOWTO_INTENT.slugFn(course, loc)}`, label: `How to Crack ${course.name} from ${loc.name}` });
  }

  // Same category (top 3 other courses)
  const cat = category || (course ? courseCategories.find((c) => c.id === course.category) : null);
  if (cat) {
    getCoursesByCategory(cat.id).filter((c) => c.slug !== course?.slug).slice(0, 3).forEach((c) => {
      links.push({ to: `/programs/best-${c.slug}-coaching-in-${loc.slug}`, label: `Best ${c.name} Coaching in ${loc.name}` });
    });
  }

  // 3 nearby locations for the same course
  if (course) {
    const nearby = (loc.nearbyAreas || []).slice(0, 3)
      .map((name) => locations.find((l) => l.name.toLowerCase() === name.toLowerCase()))
      .filter(Boolean);
    nearby.forEach((nl) => {
      links.push({ to: `/programs/best-${course.slug}-coaching-in-${nl.slug}`, label: `Best ${course.name} Coaching in ${nl.name}` });
    });
  }

  // Subject links if course context
  if (course) {
    links.push({ to: `/programs/quantitative-aptitude-coaching-in-${loc.slug}`, label: `Quant Coaching in ${loc.name}` });
    links.push({ to: `/programs/reasoning-ability-coaching-in-${loc.slug}`, label: `Reasoning Coaching in ${loc.name}` });
  }

  return links.slice(0, 12);
}

export default function ProgramPage() {
  const { slug } = useParams();
  const prog = getProgramBySlug(slug);
  if (!prog) return notFound();
  const ctx = buildContent(prog);
  if (!ctx) return notFound();

  const { h1, title, desc, intro, primaryAnswer, kicker, intentLabel, loc, course, subject, category } = ctx;
  const faqs = buildFaqs(ctx, prog);
  // For AEO pages, prepend the H1 question + primary answer as the first FAQ (boosts Q&A schema relevance)
  if (prog.type === "aeo-question") {
    faqs.unshift({ q: h1, a: primaryAnswer });
  }
  const crossLinks = buildCrossLinks(ctx, prog);
  const url = `${SITE}/programs/${prog.slug}`;
  const waMsg = buildWaMessage(ctx, prog);
  const waUrl = waLink(waMsg);

  // JSON-LD payloads (GEO + AEO)
  const ldGraph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "EducationalOrganization",
        "@id": `${SITE}/#org`,
        name: "Vedanti Academy",
        url: SITE,
        logo: `${SITE}/logo.png`,
        telephone: PHONE,
        address: ADDRESS,
        sameAs: [
          "https://www.youtube.com/@vedantiacademy",
          "https://www.facebook.com/vedantiacademy",
          "https://www.instagram.com/vedantiacademy",
        ],
        areaServed: locations.map((l) => ({ "@type": "City", name: l.name })),
      },
      {
        "@type": "LocalBusiness",
        "@id": `${SITE}/#localbusiness`,
        name: `Vedanti Academy — ${course?.name || category?.name || subject?.name || "Coaching"} in ${loc.name}`,
        image: `${SITE}/og-vedanti.jpg`,
        url,
        telephone: PHONE,
        priceRange: "₹₹",
        address: ADDRESS,
        geo: { "@type": "GeoCoordinates", latitude: 19.1554, longitude: 73.2696 },
        areaServed: { "@type": "City", name: loc.name },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "500" },
      },
      ...(course
        ? [{
            "@type": "Course",
            name: `${course.name} Coaching in ${loc.name}`,
            description: desc,
            provider: { "@id": `${SITE}/#org` },
            educationalLevel: "Competitive Exam Preparation",
            courseMode: ["Onsite", "Online"],
            inLanguage: ["en", "hi", "mr"],
            offers: { "@type": "Offer", category: "EducationalProgram", availability: "https://schema.org/InStock", priceCurrency: "INR", price: "0", areaServed: loc.name, url },
            hasCourseInstance: {
              "@type": "CourseInstance",
              courseMode: ["Onsite", "Online"],
              courseWorkload: "PT10H",
              location: { "@type": "Place", name: `Vedanti Academy ${loc.name}`, address: ADDRESS },
            },
          }]
        : []),
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Programs", item: `${SITE}/programs` },
          { "@type": "ListItem", position: 3, name: loc.name, item: `${SITE}/locations/${loc.slug}` },
          { "@type": "ListItem", position: 4, name: h1, item: url },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@type": "WebPage",
        "@id": url,
        url,
        name: title,
        description: desc,
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".aeo-answer"] },
        primaryImageOfPage: `${SITE}/og-vedanti.jpg`,
        breadcrumb: { "@id": `${url}#breadcrumb` },
      },
    ],
  };

  return (
    <div data-testid={`program-page-${prog.slug}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta name="keywords" content={`${kicker} in ${loc.name}, ${kicker} coaching ${loc.name}, ${kicker} classes ${loc.name}, Vedanti Academy ${loc.name}, ${course?.fullName || category?.name || subject?.name || ""}`} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="geo.region" content="IN-MH" />
        <meta name="geo.placename" content={loc.name} />
        <meta name="geo.position" content="19.1554;73.2696" />
        <meta name="ICBM" content="19.1554, 73.2696" />
        <link rel="alternate" hrefLang="en-in" href={url} />
        <link rel="alternate" hrefLang="x-default" href={url} />
        <script type="application/ld+json">{JSON.stringify(ldGraph)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-vedanti-navy"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.2)_0%,_transparent_55%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main py-14 md:py-20 relative z-10">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-slate-400 mb-5 flex-wrap">
            <Link to="/" className="hover:text-white transition">Home</Link>
            <ChevronRight size={14} />
            <Link to="/programs" className="hover:text-white transition">Programs</Link>
            <ChevronRight size={14} />
            <Link to={`/locations/${loc.slug}`} className="hover:text-white transition">{loc.name}</Link>
            <ChevronRight size={14} />
            <span className="text-vedanti-mango line-clamp-1">{kicker}</span>
          </nav>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-vedanti-orange/15 text-vedanti-mango text-xs font-bold uppercase tracking-wide">{intentLabel}</span>
            <span className="text-vedanti-mango text-sm font-semibold inline-flex items-center gap-1.5">
              <MapPin size={14} /> {loc.name} {loc.distance ? `· ${loc.distance}` : ""}
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
            {h1}
          </h1>
          <p className="text-slate-300 mt-4 text-lg max-w-3xl">{intro}</p>

          {/* AEO answer block — surfaced for voice/AI engines via speakable schema */}
          <div className="aeo-answer mt-6 p-4 md:p-5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm max-w-3xl">
            <div className="flex items-center gap-2 text-vedanti-mango text-xs font-bold uppercase tracking-wide mb-2">
              <Sparkles size={14} /> Quick Answer
            </div>
            <p className="text-white/90 text-sm md:text-base leading-relaxed">{primaryAnswer}</p>
          </div>

          <div className="flex flex-wrap gap-3 md:gap-4 mt-8">
            <Link to="/demo-class" className="bg-gradient-to-r from-vedanti-orange to-amber-500 text-white px-7 py-3.5 rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all inline-flex items-center gap-2" data-testid="program-hero-demo-btn">
              Book Free Demo <ArrowRight size={18} />
            </Link>
            <a href={waUrl} target="_blank" rel="noopener noreferrer nofollow" className="bg-green-500 hover:bg-green-600 text-white px-7 py-3.5 rounded-xl font-bold transition-all inline-flex items-center gap-2 shadow-lg shadow-green-500/30" data-testid="program-hero-whatsapp-btn">
              <MessageCircle size={18} /> WhatsApp <span className="hidden sm:inline">— pre-filled</span>
            </a>
            <a href={`tel:${PHONE}`} className="border-2 border-white/30 text-white hover:bg-white/10 px-7 py-3.5 rounded-xl font-semibold transition-all inline-flex items-center gap-2" data-testid="program-hero-call-btn">
              <Phone size={18} /> Call
            </a>
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer nofollow" className="border-2 border-white/30 text-white hover:bg-white/10 px-5 py-3.5 rounded-xl font-semibold transition-all inline-flex items-center gap-2" data-testid="program-hero-directions-btn">
              <MapPin size={18} /> Get Directions
            </a>
          </div>
        </div>
      </section>

      {/* Local SEO trust strip */}
      <section className="bg-white border-b">
        <div className="container-main py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { icon: Trophy, n: "31+", l: "Achievers" },
              { icon: Star, n: "500+", l: "5★ Google reviews" },
              { icon: GraduationCap, n: "10+", l: "Years of teaching" },
              { icon: MapPin, n: `${locations.length}+`, l: "Service locations" },
            ].map((s) => (
              <div key={s.l} className="flex flex-col items-center">
                <s.icon size={20} className="text-vedanti-orange mb-1" />
                <div className="font-extrabold text-vedanti-navy">{s.n}</div>
                <div className="text-slate-500 text-xs">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Vedanti */}
      <section className="section-padding bg-gradient-to-b from-white to-slate-50">
        <div className="container-main">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-vedanti-navy mb-6">
            Why {loc.name} students choose Vedanti Academy for{" "}
            <span className="text-vedanti-orange">{course?.name || category?.name || subject?.name || "competitive exams"}</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "Expert faculty", d: "Senior teachers with 10+ years of competitive-exam experience and proven shortcut techniques." },
              { t: "Vedic-Math shortcuts", d: "Solve quantitative & reasoning questions in under 30 seconds with our signature Vedic-Math shortcut library." },
              { t: "Upto Selection Support", d: "No fixed course end-date. Continue attending classes until you crack the exam — included in every package." },
              { t: "360° hybrid learning", d: `Live online + offline classes, recorded lectures, WhatsApp & Zoom doubt support — perfect for ${loc.name} students.` },
              { t: "Daily practice + mocks", d: "Daily topic quizzes, sectional tests, full-length mocks with All-India ranking and detailed solutions." },
              { t: "Personal mentor", d: "1-on-1 mentorship, weekly performance review, and a customised study plan based on your strengths." },
            ].map((b) => (
              <div key={b.t} className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
                <CheckCircle size={18} className="text-vedanti-orange mb-2" />
                <h3 className="font-bold text-vedanti-navy text-sm mb-1">{b.t}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subject-specific topic list */}
      {subject && (
        <section className="section-padding bg-slate-50">
          <div className="container-main">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-vedanti-navy mb-6">
              {subject.name} topics covered for {loc.name} students
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {subject.topics.map((t) => (
                <div key={t} className="bg-white p-3 rounded-lg border border-slate-100 text-sm text-slate-700 flex items-center gap-2">
                  <BookOpen size={14} className="text-vedanti-orange flex-shrink-0" /> {t}
                </div>
              ))}
            </div>
            <p className="mt-4 text-slate-600 text-sm">Applies to: <strong>{subject.appliesTo}</strong></p>
          </div>
        </section>
      )}

      {/* Course context — exam pattern & syllabus quick-glance */}
      {course && (
        <section className="section-padding bg-white">
          <div className="container-main">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-vedanti-navy mb-6">
              {course.name} — quick exam snapshot
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-orange-50 border-l-4 border-vedanti-orange">
                <Target size={18} className="text-vedanti-orange mb-2" />
                <div className="font-bold text-vedanti-navy text-sm">Eligibility</div>
                <div className="text-xs text-slate-600 mt-1">{course.eligibility || "See course page"}</div>
              </div>
              <div className="p-4 rounded-xl bg-blue-50 border-l-4 border-vedanti-navy">
                <Clock size={18} className="text-vedanti-navy mb-2" />
                <div className="font-bold text-vedanti-navy text-sm">Batch</div>
                <div className="text-xs text-slate-600 mt-1">{course.batchInfo || "Monthly batches"}</div>
              </div>
              <div className="p-4 rounded-xl bg-amber-50 border-l-4 border-vedanti-mango">
                <BookOpen size={18} className="text-vedanti-mango mb-2" />
                <div className="font-bold text-vedanti-navy text-sm">Mode</div>
                <div className="text-xs text-slate-600 mt-1">Online + Offline (Hybrid)</div>
              </div>
              <div className="p-4 rounded-xl bg-green-50 border-l-4 border-green-500">
                <MapPin size={18} className="text-green-600 mb-2" />
                <div className="font-bold text-vedanti-navy text-sm">Service area</div>
                <div className="text-xs text-slate-600 mt-1">{loc.name} {loc.connectivity ? `· ${loc.connectivity}` : ""}</div>
              </div>
            </div>

            {/* Syllabus highlights */}
            {course.syllabus && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(course.syllabus).slice(0, 4).map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-slate-100 bg-white p-4">
                    <h3 className="font-bold text-vedanti-navy text-sm mb-2">{k}</h3>
                    <ul className="space-y-1 text-xs text-slate-600">
                      {(v || []).slice(0, 5).map((t) => <li key={t} className="flex gap-2"><CheckCircle size={12} className="text-vedanti-orange flex-shrink-0 mt-0.5" /><span>{t}</span></li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={`/courses/${course.slug}`} className="text-vedanti-orange font-semibold text-sm inline-flex items-center gap-1 hover:underline">
                Full {course.name} syllabus & pattern <ArrowRight size={14} />
              </Link>
              <Link to={`/locations/${loc.slug}/${course.slug}`} className="text-vedanti-navy font-semibold text-sm inline-flex items-center gap-1 hover:underline">
                {course.name} centre details for {loc.name} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* AEO — How To / Step-by-Step (HowTo schema-friendly) */}
      {prog.type === "howto-course-location" && course && (
        <section className="section-padding bg-slate-50" data-testid="howto-steps">
          <div className="container-main">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-vedanti-navy mb-6">
              {course.name} 2026 strategy — 6 steps for {loc.name} students
            </h2>
            <ol className="space-y-3">
              {[
                { t: "Confirm eligibility & exam dates", d: course.eligibility ? `Confirm you meet eligibility (${course.eligibility}). Track the official notification & exam calendar for 2026.` : "Confirm eligibility per the official notification. Track 2026 exam calendar for prelims, mains and interview windows." },
                { t: "Build a 4–6 month study plan", d: `Split syllabus into Quant, Reasoning, English, GA & Computer (where applicable). Assign 2–3 hours daily, with weekend revisions.` },
                { t: "Attend live classes (online/offline)", d: `Join Vedanti Academy's ${course.name} 2026 batch — live from Badlapur or 100% online from ${loc.name}. Faculty cover concepts, shortcuts and previous-year patterns.` },
                { t: "Practice daily quizzes + sectional tests", d: "Complete 2–3 topic quizzes daily, plus 1 sectional test every week. Maintain an error log for weak areas." },
                { t: "Take 20+ full-length mocks", d: "From the third month onwards, take 1–2 full-length mocks per week under real-exam conditions. Analyse each mock and revise weak topics." },
                { t: "Final revision + exam-day strategy", d: "In the last 30 days, revise short-notes, current affairs and formulas. Lock a sectional time-strategy and avoid last-minute new topics." },
              ].map((s, i) => (
                <li key={s.t} className="bg-white p-4 rounded-xl border border-slate-100 flex gap-4">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-vedanti-orange to-amber-500 text-white font-bold flex items-center justify-center">{i + 1}</div>
                  <div>
                    <div className="font-bold text-vedanti-navy text-sm">{s.t}</div>
                    <div className="text-slate-600 text-xs mt-1 leading-relaxed">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Internal links — cross-linking matrix */}
      {crossLinks.length > 0 && (
        <section className="section-padding bg-white">
          <div className="container-main">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-vedanti-navy mb-6">
              Related programs in {loc.name}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {crossLinks.map((l) => (
                <Link key={l.to} to={l.to} className="bg-slate-50 hover:bg-orange-50 border border-slate-100 rounded-lg p-3 text-sm text-vedanti-navy hover:text-vedanti-orange transition-all flex items-center justify-between gap-2">
                  <span className="font-medium">{l.label}</span>
                  <ArrowRight size={14} className="text-vedanti-orange flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="section-padding bg-slate-50" data-testid="program-faq">
        <div className="container-main max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <HelpCircle size={24} className="text-vedanti-orange" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-vedanti-navy">
              {h1} — FAQs
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="bg-white rounded-xl border border-slate-100 px-5 shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-vedanti-navy hover:text-vedanti-orange text-sm py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-vedanti-orange via-amber-500 to-vedanti-mango"></div>
        <div className="container-main py-14 relative z-10 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-extrabold text-white mb-3">Start {course?.name || category?.name || subject?.name || "preparation"} from {loc.name} today</h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">Free demo class, expert faculty, and &apos;Upto Selection Support&apos;. No commitment.</p>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            <Link to="/demo-class" className="bg-white text-vedanti-navy hover:bg-vedanti-navy hover:text-white rounded-xl px-7 py-3.5 font-bold shadow-xl hover:-translate-y-0.5 transition-all" data-testid="program-bottom-demo-btn">Book Free Demo Class</Link>
            <a href={waUrl} target="_blank" rel="noopener noreferrer nofollow" className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-7 py-3.5 font-bold shadow-xl transition-all inline-flex items-center gap-2" data-testid="program-bottom-whatsapp-btn">
              <MessageCircle size={18} /> WhatsApp Now
            </a>
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer nofollow" className="border-2 border-white text-white hover:bg-white hover:text-vedanti-navy rounded-xl px-5 py-3.5 font-semibold transition-all inline-flex items-center gap-2" data-testid="program-bottom-directions-btn">
              <MapPin size={18} /> Directions
            </a>
          </div>
        </div>
      </section>

      {/* Sticky mobile bottom action bar — high-conversion thumb-zone CTAs */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] grid grid-cols-3" data-testid="program-mobile-cta-bar">
        <a href={`tel:${PHONE}`} className="py-3 flex flex-col items-center justify-center gap-0.5 text-vedanti-navy active:bg-slate-100" aria-label="Call Vedanti Academy">
          <Phone size={20} className="text-vedanti-orange" />
          <span className="text-[11px] font-bold">Call</span>
        </a>
        <a href={waUrl} target="_blank" rel="noopener noreferrer nofollow" className="py-3 flex flex-col items-center justify-center gap-0.5 text-white bg-green-500 active:bg-green-600" aria-label="WhatsApp Vedanti Academy" data-testid="program-mobile-whatsapp">
          <MessageCircle size={20} />
          <span className="text-[11px] font-bold">WhatsApp</span>
        </a>
        <Link to="/demo-class" className="py-3 flex flex-col items-center justify-center gap-0.5 text-white bg-gradient-to-r from-vedanti-orange to-amber-500 active:opacity-90" data-testid="program-mobile-demo">
          <Sparkles size={20} />
          <span className="text-[11px] font-bold">Free Demo</span>
        </Link>
      </div>
      {/* Spacer so content doesn't sit under the sticky bar on mobile */}
      <div className="lg:hidden h-16" aria-hidden="true"></div>
    </div>
  );
}
